/**
 * Events — the member event-index read surface, its page view-model, and
 * the write-intent channel every event-CRUD view dispatches through.
 * events.sync.ts (web-side) is the sole subscriber, performing the
 * Firestore writes and reconciling ok/error back keyed by requestId.
 *
 * `availability` never appears in `EventWrite` — it is the dedicated
 * availability trigger's field alone; the member-update rule denies a
 * write that touches it (event.entity.ts), so no form here ever carries it.
 */

import { formatDateRange } from "@mhmo91/rx";
import { computed, state } from "@mhmo91/schmancy/state";
import type { LocalizedText } from "@momo/i18n";
import {
	type Dependents,
	type Event,
	Location,
	type TicketTemplateVariant,
	type TicketType,
} from "@momo/types";
import { Subject } from "rxjs";
import { activeActor } from "../auth/actor.state.ts";
import {
	type LocationWithId,
	locationsState,
} from "../storefront/locations.state.ts";

export type EventWithId = Event.Event & {
	id: string;
	/**
	 * Resolved display URL for `poster` (a Storage object path per
	 * Event.Event's own field) — no entity counterpart, since resolving it
	 * (getDownloadURL) is genuine I/O this pure state file can't perform.
	 * events.api.ts (web-side) populates it at the read seam; a fixture sync
	 * populates it directly. null when no poster is set or the resolve failed.
	 */
	posterUrl: string | null;
	/**
	 * Resolved display URL for `logo` (a Storage object path per Event.Event's
	 * own field) — the same I/O-can't-live-here reasoning as `posterUrl`, and
	 * resolved by the same seam. null when no logo is set or the resolve
	 * failed, which the Stencil variant reads as "draw the generated
	 * mirrored wordmark instead" (event.entity.ts's `logo` doc comment).
	 */
	logoUrl: string | null;
};

// ── Live state singletons ─────────────────────────────────────────────────────

export const eventsState = state<readonly EventWithId[]>("events/list").memory(
	[],
);

/**
 * "denied" is a Firestore permission rejection (the actor lacks
 * tickets.events.manage's read path); "error" is everything else —
 * mirrors members.state.ts's membersStatus so both admin index pages tell
 * the two apart instead of blaming the connection for an access problem.
 */
export const eventsStatus = state<"loading" | "ready" | "error" | "denied">(
	"events/status",
).memory("loading");

// ── Page view-model ────────────────────────────────────────────────────────

/** event-tile.view.ts's own input shape — the buyer-facing poster card both
 *  event-list.view.ts's admin grid and event-form.view.ts's live preview
 *  pane render, so a row assembled from live form fields (not yet an
 *  EventWithId) type-checks against the same tile. */
export type EventTileRow = {
	id: string;
	name: string;
	status: Event.Event["status"];
	poster: string | null;
	startLabel: string;
	venue: string | null;
};

/** A room with no pin of its own reads as "Parent — Room"; ungrouped or
 *  unmatched resolves to null, mirroring event-tile.view.ts's own
 *  when(r.venue, …) omission of the venue line entirely. */
export function resolveVenueLabel(
	locationId: string,
	catalog: readonly LocationWithId[],
): string | null {
	const location = catalog.find((l) => l.id === locationId);
	if (!location) return null;
	const parent = location.within
		? catalog.find((l) => l.id === location.within)
		: undefined;
	return parent ? `${parent.name} — ${location.name}` : location.name;
}

/** The IANA zone an event's own naive wall-clock fields (its schedule) resolve
 *  against — a dangling locationId falls back to LocationSchema's own
 *  default, the same forward-only posture the schema's `.default()` commits
 *  to. Mirrors resolveVenueLabel's shape. */
export function resolveVenueTimezone(
	locationId: string,
	catalog: readonly LocationWithId[],
): string {
	return (
		catalog.find((l) => l.id === locationId)?.timezone ??
		Location.LocationSchema.shape.timezone.parse(undefined)
	);
}

/**
 * The full venue projection a buyer-facing surface needs: the resolved
 * "{parent} — {room}" label (resolveVenueLabel), the venue's own address,
 * its entrance/access notes, and a DIRECTIONS deep link — omitting the
 * start, so the maps app navigates from the device's own position —
 * preferring the location's own `geo` pin, then the nearest ancestor's (a
 * room shares its building's pin, walked with a visited guard against a
 * cyclic or dangling `within`), then the labeled address, so every
 * resolvable venue still gets a working link. One URL for every device: the
 * Maps URLs directions form is a universal link that opens the native
 * Google Maps app on iOS and Android alike when installed, and the web map
 * otherwise. Shared by event-header.state.ts (the shop page) and
 * order-access.sync.ts (the ticket surface's in-session fallback) — one
 * venue projection, not a copy per consumer. */
export function resolveVenue(
	locationId: string,
	catalog: readonly LocationWithId[],
): {
	venue: string | null;
	address: string | null;
	notes: string | null;
	mapsUrl: string | null;
} {
	const venue = resolveVenueLabel(locationId, catalog);
	const location = catalog.find((l) => l.id === locationId);
	if (!location || !venue)
		return { venue, address: null, notes: null, mapsUrl: null };

	let geo = location.geo ?? null;
	const visited = new Set<string>();
	let cursor: LocationWithId | undefined = location;
	while (!geo && cursor?.within && !visited.has(cursor.within)) {
		visited.add(cursor.within);
		cursor = catalog.find((l) => l.id === cursor?.within);
		geo = cursor?.geo ?? null;
	}
	const destination = geo
		? `${geo.lat},${geo.lng}`
		: encodeURIComponent(`${venue}, ${location.address}`);

	return {
		venue,
		address: location.address,
		notes: location.notes ?? null,
		mapsUrl: `https://www.google.com/maps/dir/?api=1&destination=${destination}`,
	};
}

export const eventsPageView = computed(() => {
	const catalog = locationsState.value;
	const rows: EventTileRow[] = eventsState.value
		.toSorted(
			(a, b) => a.schedule.start.toMillis() - b.schedule.start.toMillis(),
		)
		.map((e) => ({
			id: e.id,
			name: e.name,
			status: e.status,
			poster: e.posterUrl ?? null,
			startLabel: formatDateRange(
				e.schedule.start.toDate(),
				e.schedule.end.toDate(),
				{
					month: "short",
					timeZone: resolveVenueTimezone(e.locationId, catalog),
				},
			),
			venue: resolveVenueLabel(e.locationId, catalog),
		}));
	return { rows, status: eventsStatus.value };
});

/**
 * Events-index phase — loading / a live network error / a Firestore
 * permission denial / an empty catalog / a populated one, so the list
 * renders exactly one of its five frames with no derivation of its own.
 * Mirrors members.state.ts's memberListState.
 */
export const eventsListState = computed<
	"loading" | "error" | "denied" | "empty" | "populated"
>(() => {
	if (eventsStatus.value === "loading") return "loading";
	if (eventsStatus.value === "error") return "error";
	if (eventsStatus.value === "denied") return "denied";
	return eventsPageView.get().rows.length === 0 ? "empty" : "populated";
});

/**
 * Whether the signed-in actor may manage events — mirrors event.entity.ts's
 * member-write gate (tickets.events.manage). Gates both the Events
 * nav-rail item and the events-view route landing (admin-shell.view.ts),
 * the same pattern members.state.ts's canViewMembers uses for Team.
 */
export const canManageEvents = computed(
	() =>
		activeActor.get()?.permissions.includes("tickets.events.manage") ?? false,
);

// ── write-intent channel ────────────────────────────────────────────────────
// Every write an event-CRUD view needs is zero-I/O intent composed here and
// pushed to eventsAction$; events.sync.ts (web-side) is the sole subscriber,
// performing the Firestore write and reconciling ok/error back via
// eventsResult$, keyed by requestId.

/**
 * A chosen image, as a write intent — never a Storage path or URL, since
 * minting either is I/O this pure state file can't perform. Shared by
 * `EventWrite.artwork` (the poster) and `EventWrite.logo` (the event's own
 * wordmark) — same shape, same discipline, one per field. "keep" leaves the
 * field untouched (the default — also what a brand-new event with nothing
 * chosen writes, since there's nothing to touch); "set" carries a
 * ready-to-upload Blob (from schmancy-image-crop's `crop` event for the
 * poster, a plain file pick for the logo, or a picked AI candidate for the
 * poster); "remove" clears the field. events.api.ts is the sole place that
 * uploads, resolves an extension, or clears either field.
 */
export type EventArtwork =
	| { readonly kind: "keep" }
	| { readonly kind: "set"; readonly blob: Blob }
	| { readonly kind: "remove" };

/**
 * A ticket type composed locally, in ticket-type-form.view.ts's own "draft"
 * mode, before the parent event doc exists — a Pick of
 * TicketType.TicketType's create-time-essential fields
 * (kind/name/vatRate/price/capacity), plus `showAfterSoldOut` so a draft can
 * chain behind a sibling draft (both reference each other by `draftId`, the
 * only identity either has yet).
 *
 * `locationId` mirrors TicketType's own venue override — null means the
 * event's own venue (not yet chosen either, at draft time). `templateVariant`
 * is the same shape for TicketType's own rendering-variant override — null
 * means inherit the event's own (also possibly unset at draft time, in which
 * case it falls through further, to the package's built-in default; see
 * resolveTicketTemplateVariant). `salesWindow` is
 * the override entered as NAIVE wall-clock strings, never a real instant —
 * the venue, and therefore its IANA timezone, doesn't exist yet, so the
 * naive→instant conversion (Location.wallClockToInstant) happens at mint
 * time in the write reconciler, once the created event's own locationId (and
 * so the venue) is known. Null is the inherit state itself — mirrors
 * TicketType.salesWindow's own absent-means-inherit contract — and is
 * written to Firestore as absence, never a copy of the event's resolved
 * window (that copy is what would stop tracking a later event.schedule
 * edit).
 *
 * `volumeDiscounts` and the door policy stay edit-mode-only (added via
 * ticket-type-form once a real ticketTypes subcollection exists); the write
 * reconciler mints `[]` and scan-and-go for them. `draftId` is a client-only list key for the create-mode composer's
 * add/edit/remove rows — never a stored field.
 */
export type TicketTypeDraft = Pick<
	TicketType.TicketType,
	"kind" | "name" | "vatRate" | "price" | "capacity"
> & {
	draftId: string;
	/** The operator's audience answer, as live mode's own write carries it.
	 *  Null is unanswered — the mint writes absence, and
	 *  TicketType.resolveClaimableBy reads an unanswered free type as staff-only,
	 *  the fail-safe the form's chips already show selected. Never `code`: a
	 *  draft event holds no vouchers yet, so nothing could reveal it. */
	claimableBy: "buyer" | "staff" | null;
	/** Explicit rather than picked off TicketType: the schema's own field is
	 *  `nullish`, and a draft that can be `undefined` as well as `null` gives
	 *  the mint two ways to say the same absence. */
	description: string | null;
	showAfterSoldOut: string | null;
	locationId: string | null;
	templateVariant: TicketTemplateVariant | null;
	salesWindow: { opensAt: string; closesAt: string } | null;
	/** When this type ADMITS at the door — the same naive-wall-clock-strings
	 *  shape `salesWindow` above carries, for the same reason (no venue, so no
	 *  IANA zone, exists yet; the write reconciler converts at mint time). Null
	 *  is the inherit state itself — the event's own schedule, resolved at read
	 *  time by TicketType.resolveEntryWindow — written as absence, never a copy. */
	entryWindow: { opensAt: string; closesAt: string } | null;
};

/** True when pointing `draftId` (null for a not-yet-composed draft) at
 *  `candidateDraftId` would self-reference or complete a cycle through the
 *  other local drafts' own showAfterSoldOut chain — the draft-mode mirror of
 *  ticket-types.state.ts's chainCreatesCycle, keyed by draftId instead of a
 *  saved id. */
export function draftChainCreatesCycle(
	draftId: string | null,
	candidateDraftId: string,
	drafts: readonly TicketTypeDraft[],
): boolean {
	if (!candidateDraftId) return false;
	if (draftId !== null && candidateDraftId === draftId) return true;
	const seen = new Set<string>();
	let cursor: string | undefined = candidateDraftId;
	while (cursor) {
		if (draftId !== null && cursor === draftId) return true;
		if (seen.has(cursor)) return true; // a pre-existing cycle — refuse rather than loop forever
		seen.add(cursor);
		cursor =
			drafts.find((d) => d.draftId === cursor)?.showAfterSoldOut ?? undefined;
	}
	return false;
}

/** Candidate showAfterSoldOut predecessors for editing `draftId` (null when
 *  composing a new draft) — excludes self and anything that would cycle. */
export function draftChainOptions(
	draftId: string | null,
	drafts: readonly TicketTypeDraft[],
): readonly TicketTypeDraft[] {
	return drafts.filter(
		(d) => !draftChainCreatesCycle(draftId, d.draftId, drafts),
	);
}

/** The local draft predecessor a `showAfterSoldOut` draftId points at, or
 *  undefined when unset or not found — the draft-mode mirror of
 *  ticket-types.state.ts's chainPredecessor. */
export function draftChainPredecessor(
	showAfterSoldOut: string,
	drafts: readonly TicketTypeDraft[],
): TicketTypeDraft | undefined {
	return drafts.find((d) => d.draftId === showAfterSoldOut);
}

/**
 * Event.EventRefundConfig as a write intent — the entity's own shape with its
 * two instants carried as Dates instead of Timestamps, the same divergence
 * `EventWrite.schedule` already carries (minting a Timestamp is the store
 * seam's job, not this pure state file's). `message` and `refundableTypeIds`
 * are the entity's fields untouched.
 *
 * Null on `EventWrite.refund` is the absent config itself — refunds closed,
 * which is what an event with no `refund` map means to the resolver.
 */
export type EventRefundWrite = Omit<
	Event.EventRefundConfig,
	"window" | "paidBefore"
> & {
	window: { start: Date; end: Date };
	paidBefore: Date | null;
};

export type EventWrite = {
	name: string;
	description: string | null;
	/** The description per UI language, as the operator accepted it. Undefined leaves
	 *  whatever is stored untouched; an explicit null clears the map, which is
	 *  how an edit to `description` retires copy the operator no longer
	 *  stands behind. The event's own name is never localized.
	 *
	 *  Explicitly `| undefined` under `exactOptionalPropertyTypes`: the form
	 *  computes "leave it alone" as a value on every save rather than choosing
	 *  between two object literals, so undefined is passed, not omitted. */
	descriptionByLocale?: LocalizedText | null | undefined;
	schedule: { start: Date; end: Date };
	locationId: string;
	/** The event's own default rendering variant — null inherits the
	 *  package's built-in default (resolveTicketTemplateVariant). A ticket
	 *  type's own templateVariant overrides this per type. */
	templateVariant: TicketTemplateVariant | null;
	status: Event.Event["status"];
	maxTicketsPerOrder: number;
	artwork: EventArtwork;
	/**
	 * The event's own wordmark image — same write-intent shape as `artwork`,
	 * always present on the write intent for the same reason. Distinct from
	 * `templateVariant` above: absent (`keep`/no upload) is what a variant's
	 * generated mirrored wordmark reads as (event.entity.ts's `logo` field).
	 */
	logo: EventArtwork;
	/**
	 * Create-mode's composed ticket-type ladder — always present on the write
	 * intent (mirrors `artwork`'s discipline), but only meaningful on
	 * "create": the event doc's own id must exist before a ticketTypes
	 * subcollection write can target it, so an "update" body always carries
	 * `[]` — edit mode manages ticket types live via ticketTypesAction$
	 * instead, never through this channel.
	 */
	ticketTypes: readonly TicketTypeDraft[];
	/**
	 * The self-service return policy — null closes returns. Only meaningful on
	 * "update", the mirror of `ticketTypes`' create-only constraint and for the
	 * same reason: `refundableTypeIds` names ticket types by their real
	 * subcollection ids, which a create-mode draft does not have yet, so a
	 * "create" body always carries null.
	 */
	refund: EventRefundWrite | null;
};

/**
 * "setPoster" carries the chosen artwork alone, so the reconciler patches the
 * `poster` field and nothing else — the rest of the form is still an unsaved
 * draft the admin hasn't committed, and a full "update" would land it. Only an
 * existing event can be patched this way; create mode has no doc yet, so there
 * the blob still rides `EventWrite.artwork` to the create write.
 */
export type EventsAction =
	| { kind: "create"; requestId: string; body: EventWrite }
	| { kind: "update"; requestId: string; id: string; body: EventWrite }
	| { kind: "setPoster"; requestId: string; id: string; blob: Blob }
	| { kind: "delete"; requestId: string; id: string };

export const eventsAction$ = new Subject<EventsAction>();

export interface EventsResult {
	readonly requestId: string;
	readonly kind: EventsAction["kind"];
	readonly ok: boolean;
	/** the generated doc id — carried on a successful 'create' result */
	readonly id?: string;
	readonly error?: string;
	/** The census the delete door read, present only on a REFUSED delete —
	 * what still names the document, in counts. Its presence is the
	 * discriminant: a refusal is a normal answer with real numbers, not a
	 * failure, so `ok` is false and `error` is absent, and the form opens
	 * <delete-refusal> over this rather than a generic toast. */
	readonly dependents?: Dependents.Dependents;
}

export const eventsResult$ = new Subject<EventsResult>();

// ── AI artwork-generation channel ───────────────────────────────────────────
// event-form.view.ts is zero-I/O, so a "Generate with AI" click is an intent
// here, not a callable invocation — events.sync.ts (web-side) is the sole
// subscriber that calls the real generateEventArtwork callable; the designer
// studio's own event-config.sync.ts answers it with a mock instead, keeping the
// proof functional with no backend.

export type ArtworkGenerateRequest = {
	readonly requestId: string;
	readonly prompt: string;
	readonly styleHint: string | null;
};

export const artworkGenerate$ = new Subject<ArtworkGenerateRequest>();

export type ArtworkCandidate = {
	readonly id: string;
	/** an object URL — display-only, revoked by the view once no longer shown */
	readonly imageUrl: string;
	/** the ready-to-upload bytes, carried forward into EventArtwork if picked */
	readonly blob: Blob;
};

export type ArtworkGenerateResult =
	| {
			readonly requestId: string;
			readonly ok: true;
			readonly candidates: readonly ArtworkCandidate[];
	  }
	// mirrors generateEventArtwork.action.ts's HttpsError codes the FE can act
	// on distinctly: "rate-limited" (resource-exhausted, the per-workspace
	// hourly cap) and "unavailable" (every candidate failed) get their own
	// inline copy; every other failure collapses to "error".
	| {
			readonly requestId: string;
			readonly ok: false;
			readonly reason: "rate-limited" | "unavailable" | "error";
	  };

export const artworkGenerateResult$ = new Subject<ArtworkGenerateResult>();
